import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

export interface AgentJob {
  publicId: string; jobType: string; status: string;
  progressDone: number; progressTotal: number | null;
  errorCode: string | null; createdAt: string; updatedAt: string;
}
export interface AgentThroughput { jobType: string; runs: number; processed: number; }
export interface AgentActivity {
  active: AgentJob[]; recent: AgentJob[];
  pendingApprovals: number; pendingReviews: number;
  throughput: AgentThroughput[];
}

/**
 * Leads Agent strip: the AI's live work made visible on every Leads surface.
 * Polls automation_job truth (via /api/leads-agent/activity)
 * every 15s: active jobs with real progress, recent outcomes, and the
 * human gates (approvals / identity reviews). Best-effort: a feed error
 * only hides the strip - it never breaks the page hosting it.
 */
@Component({
  selector: 'app-leads-agent-strip',
  templateUrl: './leads-agent-strip.component.html',
  styleUrls: ['./leads-agent-strip.component.css'],
})
export class LeadsAgentStripComponent implements OnInit, OnDestroy {
  /**
   * What this strip calls itself. A page that only plans territories should not
   * claim to be the whole Leads Agent, because a manager reading "idle" there
   * would take it to mean nothing anywhere is running.
   *
   * The default named the module this used to live in - the class, the file and
   * the endpoint were all renamed to Leads and this string was not, so the one
   * place a user reads still said Pipeline.
   */
  @Input() heading = 'Leads Agent';

  /**
   * Job types this strip is about. Null means every type, which is what the
   * Overview and the Board want - they oversee the whole agent. A
   * page-scoped strip names its own, so Territories never reports "Planning a
   * sales day" and Sales Route never reports "territories drawn": on a page
   * about one thing, another thing's progress is noise that reads like an error.
   */
  @Input() jobTypes: readonly string[] | null = null;

  /** Job type → human phrasing. Unknown types fall back to the raw name. */
  readonly jobLabels: Record<string, string> = {
    PlacePull: 'Pulling places', PlaceEnrichment: 'Enriching places',
    LeadBuild: 'Building leads', ImportApply: 'Applying import',
    LeadQualify: 'Qualifying leads', CampaignDiscovery: 'Discovering for campaign',
    TerritoryPlan: 'Planning territories', SalesRoutePlan: 'Planning a sales day',
  };

  agent: AgentActivity | null = null;
  /** Job ids seen in a previous poll, so only genuinely new feed rows animate
   *  in. Without this every 15s tick would re-animate the whole list. */
  private seen = new Set<string>();
  /** Ids to animate on this render pass. */
  fresh = new Set<string>();
  private timer?: ReturnType<typeof setInterval>;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.load();
    this.timer = setInterval(() => this.load(), 15000);
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
  }

  load(): void {
    this.http.get<AgentActivity>('/api/leads-agent/activity').subscribe({
      next: res => {
        this.markFresh(res);
        this.agent = res;
      },
      error: () => { /* best-effort; host page stays up */ },
    });
  }

  /** Diff this poll against the last: anything unseen streams in. The very
   *  first load is not "new" - the whole feed would flash on page open. */
  private markFresh(res: AgentActivity): void {
    const ids = [...res.active, ...res.recent].map(j => j.publicId);
    const first = this.agent === null;
    this.fresh = first ? new Set<string>() : new Set(ids.filter(id => !this.seen.has(id)));
    this.seen = new Set(ids);
  }

  isFresh(job: AgentJob): boolean {
    return this.fresh.has(job.publicId);
  }

  /** True when this strip is about the given job type. */
  private mine(jobType: string): boolean {
    return this.jobTypes === null || this.jobTypes.includes(jobType);
  }

  /** Live work this strip is about. Drives the dot, the "working" state and the bars. */
  get activeJobs(): AgentJob[] {
    return (this.agent?.active ?? []).filter(job => this.mine(job.jobType));
  }

  /** The 24h counters this strip is about - "territories drawn" does not belong
   *  on a page about a rep's day. */
  get visibleThroughput(): AgentThroughput[] {
    return (this.agent?.throughput ?? []).filter(t => this.mine(t.jobType));
  }

  /** The tail shows one outcome per job type - the newest. The feed arrives
   *  newest-first, and listing every finished run stacked a job's whole
   *  history into what read as a live error: two long-superseded failures
   *  standing shoulder to shoulder with the success that followed them. A
   *  stale Failed is history, and history is one line per activity. */
  get recentLatest(): AgentJob[] {
    const latest: AgentJob[] = [];
    const seen = new Set<string>();
    for (const job of this.agent?.recent ?? []) {
      if (!this.mine(job.jobType)) continue;
      if (seen.has(job.jobType)) continue;
      seen.add(job.jobType);
      latest.push(job);
      if (latest.length === 3) break;
    }
    return latest;
  }

  /** "When" for the tail tooltip, so an old outcome is readable as old. */
  finishedAt(job: AgentJob): string {
    return new Date(job.updatedAt).toLocaleString();
  }

  jobLabel(job: AgentJob): string {
    return this.jobLabels[job.jobType] ?? job.jobType;
  }

  /** "Pulling places" → "Places pulled": the counters report finished work. */
  readonly throughputLabels: Record<string, string> = {
    PlacePull: 'places pulled', PlaceEnrichment: 'places enriched',
    LeadBuild: 'leads built', ImportApply: 'rows applied',
    LeadQualify: 'leads qualified', CampaignDiscovery: 'campaign finds',
    TerritoryPlan: 'territories drawn',
  };

  throughputLabel(t: AgentThroughput): string {
    return this.throughputLabels[t.jobType] ?? t.jobType;
  }

  /** True when the agent produced anything in the window - drives whether the
   *  counters row is worth showing at all. */
  get hasThroughput(): boolean {
    return this.visibleThroughput.some(t => t.processed > 0);
  }

  jobProgress(job: AgentJob): number | null {
    if (job.progressTotal == null || job.progressTotal <= 0) return null;
    return Math.min(100, Math.round((job.progressDone / job.progressTotal) * 100));
  }

  /** Jobs an operator has asked to stop, until the next poll confirms it. */
  readonly stopping = new Set<string>();

  /**
   * Stops one running job.
   *
   * Here as well as on the page that starts a job, because those are different
   * situations: the start button only knows the job THIS tab started, and the
   * job that needs stopping is usually the one somebody else kicked off, or one
   * that outlived the tab that did. The strip lists it either way.
   *
   * Best-effort, like the rest of the strip: a failure leaves the row alone and
   * the next poll tells the truth. Nothing on the host page depends on it.
   */
  stopJob(job: AgentJob): void {
    if (this.stopping.has(job.publicId)) return;
    this.stopping.add(job.publicId);
    this.http.post(`/api/automation/jobs/${job.publicId}/cancel`, {}).subscribe({
      next: () => { this.stopping.delete(job.publicId); this.load(); },
      error: () => { this.stopping.delete(job.publicId); this.load(); },
    });
  }

  openApprovals(): void {
    this.router.navigate(['/leads']);
  }

  /** The identity review queue lives in the Leads List now: same rows,
   *  filtered to the prospects the resolver flagged for a human. */
  openReviewQueue(): void {
    this.router.navigate(['/leads'], { queryParams: { status: 'Review' } });
  }
}
